import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Relational campus picker for the Admin School (Add/Edit) forms.
 *
 * Replaces the legacy free-text `brand_id` input. Fetches Campus docs from
 * `/api/admin/campuses` once on mount and caches them. Emits the Campus's
 * `campus_id` via onChange when a real option is chosen (`__none__` clears).
 *
 * Props:
 *   value       string | null   — selected campus_id
 *   onChange    (id, campus?) → void
 *   testid      data-testid prefix for the trigger
 */
export default function CampusPicker({ value, onChange, testid = "campus-picker" }) {
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/admin/campuses")
      .then((r) => { if (alive) setCampuses(r.data || []); })
      .catch(() => { if (alive) setCampuses([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const handleChange = (v) => {
    if (v === "__none__") {
      onChange(null, null);
      return;
    }
    const picked = campuses.find((c) => c.campus_id === v) || null;
    onChange(v, picked);
  };

  return (
    <Select value={value || "__none__"} onValueChange={handleChange}>
      <SelectTrigger data-testid={testid}>
        <SelectValue placeholder={loading ? "Memuat cabang…" : "Pilih cabang (Campus)"} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">— Tidak ada (sekolah mandiri) —</SelectItem>
        {campuses.map((c) => (
          <SelectItem key={c.campus_id} value={c.campus_id} data-testid={`${testid}-opt-${c.campus_id}`}>
            {c.name}{c.city ? ` · ${c.city}` : ""}
            {c.unit_count > 0 ? ` (${c.unit_count} unit)` : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
